import { AttractionCategoriesList } from '@/components/attraction-categories';
import { AttractionsList } from '@/components/attractions';
import {
  attractionCategoryCrudConfig,
  type AttractionCategory,
} from '@/config/objects/attraction-categories';
import { attractionTypeCrudConfig, type AttractionType } from '@/config/objects/attraction-types';
import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Attraction Type detail page
 */
export function AttractionTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [attractionType, setAttractionType] = React.useState<AttractionType | null>(null);

  /**
   * Delete attraction type mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (attractionTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attraction-types/${attractionTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate attraction types list to refresh data
        queryClient.invalidateQueries({ queryKey: ['AttractionType'] });
        navigate('/attraction-types');
      } else {
        throw new Error(response.error || 'Failed to delete attraction type');
      }
    },
  });

  // Create custom config for attraction type-specific attractions
  const attractionTypeAttractionsConfig: CrudConfig<Attraction> = useMemo(
    () => ({
      ...attractionCrudConfig,
      entityName: 'Attraction',
      entityNamePlural: 'Attractions',
      endpoints: {
        ...attractionCrudConfig.endpoints,
        list: `/attraction-types/${id}/attractions`,
        delete: (attractionId: string) => `/attraction-types/${id}/attractions/${attractionId}`,
      },
      listView: {
        ...attractionCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attractions',
            // Exclude already added attractions
            where: attractionType
              ? {
                  types: {
                    none: {
                      attractionTypeId: attractionType.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attraction: Attraction) => {
              await apiClient.post(`/attraction-types/${id}/attractions`, {
                attractionId: attraction.id,
              });
              toast.success('Attraction added to attraction type');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attraction: Attraction) => {
              await apiClient.delete(`/attraction-types/${id}/attractions/${attraction.id}`);
              toast.success('Attraction removed from attraction type');
            },
          },
        },
      },
    }),
    [attractionType, id, apiClient],
  );

  // Create custom config for attraction type-specific categories
  const attractionTypeCategoriesConfig: CrudConfig<AttractionCategory> = useMemo(
    () => ({
      ...attractionCategoryCrudConfig,
      entityName: 'Attraction Category',
      entityNamePlural: 'Attraction Categories',
      endpoints: {
        ...attractionCategoryCrudConfig.endpoints,
        list: `/attraction-types/${id}/categories`,
        delete: (categoryId: string) => `/attraction-types/${id}/categories/${categoryId}`,
      },
      listView: {
        ...attractionCategoryCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attraction-categories',
            // Exclude already added categories
            where: attractionType
              ? {
                  types: {
                    none: {
                      attractionTypeId: attractionType.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (category: AttractionCategory) => {
              await apiClient.post(`/attraction-types/${id}/categories`, {
                attractionCategoryId: category.id,
              });
              toast.success('Category added to attraction type');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (category: AttractionCategory) => {
              await apiClient.delete(`/attraction-types/${id}/categories/${category.id}`);
              toast.success('Category removed from attraction type');
            },
          },
        },
      },
    }),
    [attractionType, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Attraction Type Not Found</h2>
          <p className="text-muted-foreground">The requested attraction type could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this attraction type
   */
  const handleEdit = (attractionType: AttractionType) => {
    navigate(`/attraction-types/${attractionType.id}/edit`);
  };

  /**
   * Handle attraction type deletion
   */
  const handleDelete = async (attractionType: AttractionType) => {
    return toast.promise(deleteMutation.mutateAsync(attractionType.id), {
      loading: 'Deleting attraction type...',
      success: 'Attraction type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction type',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attraction Types', href: '/attraction-types' },
    { label: attractionType?.name || 'Attraction Type', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={attractionTypeCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(attractionTypeData: AttractionType) => setAttractionType(attractionTypeData)}
        />

        {/* Attraction Type Attractions Section */}
        <AttractionsList
          config={attractionTypeAttractionsConfig}
          className="p-0"
          baseUrl="/attractions"
        />

        {/* Attraction Type Categories Section */}
        <AttractionCategoriesList
          config={attractionTypeCategoriesConfig}
          className="p-0"
          baseUrl="/attraction-categories"
        />
      </div>
    </PageContainer>
  );
}
